import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from './cn';
import { Spinner } from './Spinner';

export const buttonVariants = cva(
  [
    'atte-press relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap',
    'rounded-sm font-medium',
    'disabled:pointer-events-none aria-disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-on-primary hover:bg-primary-hover disabled:bg-raised disabled:text-faint disabled:ring-1 disabled:ring-inset disabled:ring-border',
        secondary:
          'border border-control bg-surface text-ink hover:bg-hover disabled:border-border disabled:bg-raised disabled:text-faint',
        ghost: 'bg-transparent text-ink-2 hover:bg-active hover:text-ink disabled:text-faint',
        destructive:
          'bg-bad text-on-bad hover:bg-bad-hover disabled:bg-raised disabled:text-faint disabled:ring-1 disabled:ring-inset disabled:ring-border',
        link: 'h-auto px-0 text-primary underline-offset-4 hover:underline disabled:text-faint',
      },
      size: {
        sm: 'h-8 px-2.5 text-label coarse:h-10',
        md: 'h-9 px-3.5 text-body coarse:h-11',
        lg: 'h-11 px-[18px] text-button-lg coarse:h-12',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0 coarse:h-auto' }],
    defaultVariants: { variant: 'secondary', size: 'md', fullWidth: false },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconStart?: LucideIcon;
  iconEnd?: LucideIcon;
  loading?: boolean;
  /** Texto mientras carga; si no se pasa, conserva el texto. */
  loadingText?: ReactNode;
  fullWidth?: boolean;
  /** Renderiza el hijo (p. ej. un <Link>) con los estilos del botón. */
  asChild?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

/** Botón (§6.2). Un solo `primary` por pantalla u hoja. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    size,
    iconStart: IconStart,
    iconEnd: IconEnd,
    loading = false,
    loadingText,
    fullWidth,
    asChild = false,
    type = 'button',
    className,
    children,
    disabled,
    onClick,
    ...rest
  },
  ref,
) {
  const iconSize = size === 'lg' ? 18 : 16;
  const classes = cn(buttonVariants({ variant, size, fullWidth }), className);

  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} {...rest}>
        {children}
      </Slot.Root>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled}
      aria-disabled={disabled || loading || undefined}
      aria-busy={loading || undefined}
      onClick={(e) => {
        if (loading) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...rest}
    >
      {loading ? (
        <Spinner size={16} />
      ) : IconStart ? (
        <IconStart size={iconSize} strokeWidth={1.75} aria-hidden />
      ) : null}
      {loading && loadingText ? loadingText : children}
      {IconEnd && !loading ? <IconEnd size={iconSize} strokeWidth={1.75} aria-hidden /> : null}
    </button>
  );
});
